import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBriefcase, faArrowUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";
import { VerticalTimelineElement } from "react-vertical-timeline-component";
import { useTranslation } from "react-i18next";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import Collapse from "@mui/material/Collapse";

type Section = { title: string; tasks: string[] };
type OpenType = { [index: number]: boolean };

type TimelineElementProps = {
  job: {
    jobDate: string;
    title: string;
    company: string;
    jobDescription: string;
    durationLabel?: string;
    tags?: string[];
    icon?: IconProp;
    sectionsKey: string;
    url?: string;
  };
};

const TimelineElement: React.FC<TimelineElementProps> = ({ job }) => {
  const { t } = useTranslation();
  const sections = t(job.sectionsKey, { returnObjects: true }) as Section[];

  const [open, setOpen] = React.useState<OpenType>({});
  const toggle = (index: number) =>
    setOpen((prev) => ({ ...prev, [index]: !prev[index] }));

  return (
    <VerticalTimelineElement
      className="vertical-timeline-element--work"
      contentStyle={{ background: "var(--bg-card)", color: "var(--text)", border: "1px solid var(--border)", borderRadius: "10px", boxShadow: "none" }}
      contentArrowStyle={{ borderRight: "7px solid var(--border)" }}
      date={t(job.jobDate)}
      iconStyle={{ background: "var(--accent)", color: "#fff" }}
      icon={<FontAwesomeIcon icon={job.icon ?? faBriefcase} />}
    >
      <div className="tl-header">
        <div className="tl-title-row">
          <h3 className="tl-title">{t(job.title)}</h3>
          {job.durationLabel && (
            <span className="tl-duration">{job.durationLabel}</span>
          )}
        </div>
        {job.url ? (
          <a href={job.url} target="_blank" rel="noopener noreferrer" className="tl-company tl-company-link">
            {t(job.company)}
            <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="tl-company-link-icon" />
          </a>
        ) : (
          <h4 className="tl-company">{t(job.company)}</h4>
        )}
      </div>

      <p className="tl-description">{t(job.jobDescription)}</p>

      {job.tags && job.tags.length > 0 && (
        <div className="tl-tags">
          {job.tags.map((tag, i) => (
            <span key={i} className="tl-tag">{tag}</span>
          ))}
        </div>
      )}

      <div className="tl-missions">
        {Array.isArray(sections) && sections.map((section, index) => (
          <div key={index} className="tl-section">
            <button
              className="tl-section-toggle"
              onClick={() => toggle(index)}
            >
              <span>{section.title}</span>
              {open[index] ? (
                <ExpandLessIcon fontSize="small" />
              ) : (
                <ExpandMoreIcon fontSize="small" />
              )}
            </button>
            <Collapse in={!!open[index]} timeout="auto" unmountOnExit>
              <ul className="tl-task-list">
                {section.tasks.map((task, i) => (
                  <li key={i} className="tl-task">{task}</li>
                ))}
              </ul>
            </Collapse>
          </div>
        ))}
      </div>
    </VerticalTimelineElement>
  );
};

export default TimelineElement;
